import { on } from 'delegated-events'
import Pristine from 'pristinejs'
import verge from 'verge'
import requestTimeout from '../common/requestTimeout'
import scrollTo from '../common/scrollTo'

const SELECTORS = Selectors({
  checkoutPage: 'store-checkout-page',
  checkoutForm: 'store-checkout-form',
  checkoutWrapper: 'store-checkout-wrapper',
  shippingFieldset: 'store-checkout-shipping',
  billingFieldset: 'store-checkout-billing',
  shippingRatesFieldset: 'store-checkout-shipping-rates',
  shippingRatesHolder: 'store-checkout-shipping-rates .shipping-rates',
  shippingCarrier: 'input[name=ShippingCarrier]',
  shippingRateField: 'input[name=ShippingRateCode]',
  sameAsShippingInput: 'input[name=same_as_shipping]',
  shippingCountryField: 'select[name=ShippingCountry]',
  shippingStateField: 'input[name=ShippingState]',
  shippingZipField: 'input[name=ShippingZip]',
  couponApplyButton: 'apply-coupon',
  couponClearButton: 'clear-coupon',
  couponField: 'input[name=_coupon]',
  couponForm: 'coupon-form',
  paymentSourceField: 'input[name=PaymentSourceType]',
  summary: 'store-checkout-summary-wrapper .store-checkout-summary',
  summaryShippingPrice: 'checkout-shipping-price',
  summaryHandlingPrice: 'checkout-handling-price',
  summaryCouponPrice: 'checkout-coupon-price',
  summaryTotalPrice: 'checkout-total-price',
  siteHeader: 'site-header',
  pageHeader: 'page-header',
  loading: 'is-loading',
  validating: 'is-validating',
  taxIdHolder: 'tax-id-holder',
  taxId: 'input[name=TaxID]',
})

const props = {
  $checkoutPage: document.querySelector(SELECTORS.asClass('checkoutPage')),
  $checkoutForm: document.querySelector(SELECTORS.asClass('checkoutForm')),
  $checkoutWrapper: document.querySelector(SELECTORS.asClass('checkoutWrapper')),
  $shippingCountryField: document.querySelector(SELECTORS.shippingCountryField),
  $shippingZipField: document.querySelector(SELECTORS.shippingZipField),
  $shippingFieldset: document.querySelector(SELECTORS.asClass('shippingFieldset')),
  $shippingCarrier: document.querySelectorAll(SELECTORS.shippingCarrier),
  $billingFieldset: document.querySelector(SELECTORS.asClass('billingFieldset')),
  $shippingRatesFieldset: document.querySelector(SELECTORS.asClass('shippingRatesFieldset')),
  $shippingRatesHolder: document.querySelector(SELECTORS.asClass('shippingRatesHolder')),
  $summary: document.querySelector(SELECTORS.asClass('summary')),
  $summaryHandlingPrices: document.querySelectorAll(SELECTORS.asClass('summaryHandlingPrice')),
  $summaryShippingPrices: document.querySelectorAll(SELECTORS.asClass('summaryShippingPrice')),
  $summaryCouponPrices: document.querySelectorAll(SELECTORS.asClass('summaryCouponPrice')),
  $summaryTotalPrices: document.querySelectorAll(SELECTORS.asClass('summaryTotalPrice')),
  $couponApplyButton: document.querySelector(SELECTORS.asClass('couponApplyButton')),
  $couponField: document.querySelector(SELECTORS.couponField),
  $couponForm: document.querySelector(SELECTORS.asClass('couponForm')),
  $siteHeader: document.querySelector(SELECTORS.asClass('siteHeader')),
  $pageHeader: document.querySelector(SELECTORS.asClass('pageHeader')),
  $taxIdHolder: document.querySelector(SELECTORS.asClass('taxIdHolder')),
  $taxId: document.querySelectorAll(SELECTORS.taxId),

  stripe: null,
  stripeCard: null,
  pristine: null,
  pristineConfig: {
    classTo: 'field',
    errorClass: 'has-error',
    successClass: 'has-success',
    errorTextParent: 'field',
    errorTextTag: 'div',
    errorTextClass: 'text-help',
  },

  requiresShipping: false,
  securityId: null,
  updateSummaryTotalsEndpoint: null,
  handleCouponEndpoint: null,
  shippingCarriers: {},
  allowExpeditedShipping: false,

  shippingState: '',
  shippingZip: '',
  shippingCountry: 'United States',
  shippingCarrier: '',
  shippingCarrierRate: '',
  couponId: '',
}

let stripe;
let elements;
let paymentElement;
let paymentIntentId;
let redirectURL;

const fn = {
  init: () => {
    props.stripe = window.Stripe(props.$checkoutPage.dataset.stripePubKey)
    props.pristine = new Pristine(props.$checkoutForm, props.pristineConfig)
    props.formSecurityId = props.$checkoutForm.querySelector('[name=SecurityID]').value
    props.fetchShippingRatesEndpoint = props.$checkoutForm.dataset.fetchShippingRatesEndpoint
    props.updateSummaryTotalsEndpoint = props.$checkoutForm.dataset.updateSummaryTotalsEndpoint
    props.handleCouponEndpoint = props.$checkoutForm.dataset.handleCouponEndpoint
    props.allowExpeditedShipping = props.$checkoutForm.dataset.allowExpeditedShipping

    if (props.$checkoutForm.querySelector('[name=HasShipping]')) {
      props.requiresShipping = true
    }

    if (document.querySelector('.tax-id-holder')) {
      document.querySelector('.tax-id-holder').style.display = 'none'
    }

    fn.bindEvents()
    fn.handleCouponField()

    fn.initializePaymentForm(props.$checkoutForm, props.stripe);

    const query = new URLSearchParams(window.location.search)
    if (query.has('error')) {
        if (query.get('error') === 'payment_failed') {
            fn.showMessage("Payment failed. Please try again.");

            // scroll to the message
            const messageContainer = document.querySelector("#payment-message");
            scrollTo(messageContainer, 1000);
        }
    }

    requestTimeout(fn.updateStickySummary, 250)
  },

  bindEvents: () => {
    on('change', SELECTORS.sameAsShippingInput, fn.handleSameAsShipping)
    on('change', SELECTORS.shippingCountryField, fn.handleCountryChange)
    on('change', SELECTORS.shippingStateField, fn.handleShippingFieldChange)
    on('change', SELECTORS.shippingZipField, fn.handleShippingFieldChange)
    on('change', SELECTORS.paymentSourceField, fn.handlePaymentSourceType)
    on('change', SELECTORS.shippingCarrier, fn.handleShippingCarrierChange)
    on('change', SELECTORS.shippingRateField, fn.handleShippingRateChange)
    on('keydown', SELECTORS.couponField, fn.handleCouponField)
    on('click', SELECTORS.asClass('couponApplyButton'), fn.handleApplyCoupon)
    on('click', SELECTORS.asClass('couponClearButton'), fn.handleClearCoupon)
    // window.addEventListener('resize', debounce(fn.updateStripeCard, 250))
    window.addEventListener('scroll', fn.updateStickySummary)
    props.$checkoutForm.addEventListener('submit', fn.handleCheckout)
  },

  updateStickySummary: () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      const summaryBox = verge.rectangle(props.$summary)
      const formBox = verge.rectangle(props.$checkoutForm)
      const diff = verge.scrollY() - props.$siteHeader.offsetHeight
      const offset = 40
      const stopPoint =
        formBox.height - summaryBox.height + props.$siteHeader.offsetHeight + props.$pageHeader.offsetHeight + offset

      if (summaryBox.top < diff) {
        props.$summary.classList.add('is-sticking')
        props.$summary.style.width = `${props.$summary.parentNode.offsetWidth}px`
        props.$summary.style.top = `${props.$siteHeader.offsetHeight + offset}px`

        if (verge.scrollY() >= stopPoint) {
          props.$summary.classList.add('is-stuck')
          props.$summary.style.top = null
        } else {
          props.$summary.classList.remove('is-stuck')
        }
      } else {
        props.$summary.classList.remove('is-sticking')
        props.$summary.style.top = null
        props.$summary.style.width = null
      }
    } else {
      props.$summary.classList.remove('is-sticking')
      props.$summary.style.top = null
      props.$summary.style.width = null
    }
  },

  handleSameAsShipping: e => {
    if (e.target.checked) {
      props.$billingFieldset.querySelectorAll('[data-copy-target]').forEach($field => {
        const $copyField = props.$shippingFieldset.querySelector(`input[name=${$field.dataset.copyTarget}]`)
        $field.value = $copyField.value
        $field.focus()
        $field.blur()
      })

      if (props.$checkoutForm.classList.contains(SELECTORS.validating)) {
        props.pristine.validate()
      }
    }
  },

  handleCountryChange: e => {
    props.shippingCountry = e.target.value

    if (props.requiresShipping) {
      fn.fetchShippingRates()
    }

    fn.updateSummaryTotals()
  },

  toggleHandlingTotal: isVisible => {
    Array.from(document.querySelectorAll('.store-checkout-summary-totals__handling')).forEach(
      el => (el.style.display = isVisible ? 'flex' : 'none')
    )
  },

  handleShippingFieldChange: e => {
    const fieldName = e.target.name

    if (fieldName === 'ShippingState') {
      props.shippingState = e.target.value
    }

    if (fieldName === 'ShippingZip') {
      props.shippingZip = e.target.value
    }

    if (props.requiresShipping) {
      fn.fetchShippingRates()
    }
  },

  handleShippingCarrierChange: e => {
    Array.from(props.$shippingRatesFieldset.querySelectorAll('.shipping-rates-block')).forEach(
      el => (el.style.display = 'none')
    )

    Array.from(document.querySelectorAll(SELECTORS.shippingRateField)).forEach(el => (el.checked = false))

    const $carrier = props.$shippingRatesFieldset.querySelector(`[data-carrier="${e.target.value}"]`)

    if ($carrier) {
      $carrier.style.display = 'inline-block'
    }

    props.$shippingRatesFieldset.classList.remove('has-error')

    fn.handleTaxId()

    if (e.target.value === 'None') {
      props.shippingCarrier = ''
      fn.updateSummaryTotals()
    } else {
      props.shippingCarrier = e.target.value
    }
  },

  handleShippingRateChange: e => {
    const carrier = document.querySelector(`${SELECTORS.shippingCarrier}:checked`).value
    const carrierRate = e.target.value

    if (!props.shippingState || !props.shippingZip || !props.shippingCountry) {
      return
    }

    fn.setLoadingOrderSummary(true)
    props.$shippingRatesFieldset.classList.add('is-selecting-rate')
    Array.from(props.$shippingRatesFieldset.querySelectorAll('.shipping-rates-block')).forEach(el =>
      el.classList.add('is-loading')
    )
    props.$shippingRatesFieldset.classList.remove('has-error')

    props.shippingCarrier = carrier
    props.shippingCarrierRate = carrierRate

    fn.updateSummaryTotals().then(() => {
      props.$shippingRatesFieldset.classList.remove('is-selecting-rate')
      Array.from(props.$shippingRatesFieldset.querySelectorAll('.shipping-rates-block')).forEach(el =>
        el.classList.remove('is-loading')
      )
    })
  },

  fetchShippingRates: () => {
    const toState = props.shippingState
    const toZip = props.shippingZip
    const toCountry = props.shippingCountry
    const isShippingDomestic = toCountry === 'United States'

    if (!toState || !toZip || !toCountry) {
      return
    }

    const carriers = []
    if (props.allowExpeditedShipping == 'yes') {

        carriers.push('UPS')
        carriers.push('FedEx')

        if (!isShippingDomestic) {
          carriers.push('DHL')
        }

    }

    props.$shippingRatesFieldset.classList.add('is-loading')

    fn.resetShippingRates()

    ajax
      .get(props.fetchShippingRatesEndpoint, {
        params: {
          carriers,
          state: toState,
          country: toCountry,
          zip: toZip,
          SecurityID: props.formSecurityId,
        },
      })
      .then(resp => {
        props.$shippingRatesFieldset.classList.remove('is-loading')

        if (resp.data.success) {
          props.shippingCarriers = resp.data.carriers
          fn.buildShippingRates()
        }

        fn.updateSummaryTotals()
      })
  },

  resetShippingRates: () => {
    Array.from(props.$shippingCarrier).forEach(el => (el.checked = false))
    Array.from(props.$shippingRatesFieldset.querySelectorAll('.optionset.shipping-carriers li')).forEach(
      el => (el.style.display = 'none')
    )
    props.$shippingRatesHolder.innerHTML = null

    fn.toggleShippingTotal(false)
    props.$shippingRatesFieldset.classList.remove('is-loaded')

    props.shippingCarrier = ''
    props.shippingCarrierRate = ''
  },

  buildShippingRates: () => {
    let rateBlocks = ''

    props.$shippingRatesFieldset.classList.remove('has-error')

    const $noShipping = props.$shippingRatesFieldset.querySelector(`ul li.valNone`)

    if ($noShipping) {
      $noShipping.querySelector('input').checked = true
      $noShipping.style.display = 'block'
    }

    for (let carrier in props.shippingCarriers) {
      const carrierData = props.shippingCarriers[carrier]
      const rates = carrierData.rates

      if (rates) {
        props.$shippingRatesFieldset.querySelector(`ul li.val${carrier}`).style.display = 'block'

        rateBlocks += `<div class="shipping-rates-block" data-carrier="${carrier}"><label class="left">Please select a rate</label><ul class="field optionset">`

        for (let rate of rates) {
          if (rate.rate != 0) {
            //console.log(rates.length)
            if (carrier == 'DHL') {
              if (rate.desc == 'EXPRESS WORLDWIDE') {
                rateBlocks += `
                  <li>
                    <input id="rate-${carrier}-${rate.service_code}" type="radio" name="ShippingRateCode" value="${
                      rate.service_code
                    }">
                    <label for="rate-${carrier}-${rate.service_code}"><span>${
                      rate.desc
                    }&nbsp;-&nbsp;<strong>$${rate.rate.toFixed(2)}</strong></span></label>
                  </li>
                `
              }
            }
            else {
              rateBlocks += `
                <li>
                  <input id="rate-${carrier}-${rate.service_code}" type="radio" name="ShippingRateCode" value="${
                    rate.service_code
                  }">
                  <label for="rate-${carrier}-${rate.service_code}"><span>${
                    rate.desc
                  }&nbsp;-&nbsp;<strong>$${rate.rate.toFixed(2)}</strong></span></label>
                </li>
              `
            }
          }
        }

        rateBlocks += '</ul></div>'
      }
    }

    props.$shippingRatesHolder.innerHTML = rateBlocks
    props.$shippingRatesFieldset.classList.add('is-loaded')
  },

  updateSummaryTotals: () => {
    const formData = new FormData()
    formData.append('State', props.shippingState)
    formData.append('Zip', props.shippingZip)
    formData.append('Country', props.shippingCountry)
    formData.append('Carrier', props.shippingCarrier)
    formData.append('CarrierRate', props.shippingCarrierRate)
    formData.append('CouponID', props.couponId)
    formData.append('SecurityID', props.formSecurityId)

    fn.setLoadingOrderSummary(true)

    return ajax.post(props.updateSummaryTotalsEndpoint, formData).then(resp => {
      if (resp.data.success) {
        props.$summaryHandlingPrices.forEach($el => ($el.textContent = resp.data.handling_total))
        props.$summaryShippingPrices.forEach($el => ($el.textContent = resp.data.shipping_total))
        props.$summaryCouponPrices.forEach($el => ($el.textContent = resp.data.coupon_total))
        props.$summaryTotalPrices.forEach($el => ($el.textContent = resp.data.order_total))

        if (resp.data.shipping_total) {
          fn.toggleShippingTotal(true)
        } else {
          fn.toggleShippingTotal(false)
        }

        if (resp.data.handling_total) {
          fn.toggleHandlingTotal(true)
        } else {
          fn.toggleHandlingTotal(false)
        }

        if (resp.data.coupon_total) {
          fn.toggleCouponTotal(true)
        } else {
          fn.toggleCouponTotal(false)
        }

        if (resp.data.order_total == '0.00') {
          fn.disablePayment()
        } else {
          fn.enablePayment()
        }
      }

      fn.setLoadingOrderSummary(false)

      fn.updatePaymentIntent(resp.data.order_total);
    })
  },

  toggleShippingTotal: isVisible => {
    Array.from(document.querySelectorAll('.store-checkout-summary-totals__shipping')).forEach(
      el => (el.style.display = isVisible ? 'flex' : 'none')
    )
  },

  setLoadingOrderSummary: isLoading => {
    Array.from(document.querySelectorAll('.store-checkout-summary')).forEach(el =>
      isLoading ? el.classList.add('is-loading') : el.classList.remove('is-loading')
    )
  },

  handleCouponField: e => {
    if (props.$couponField) {
      if (props.$couponField.value === '') {
        props.$couponApplyButton.disabled = true
      } else {
        props.$couponApplyButton.disabled = false
      }

      if (e && e.keyCode === 13) {
        e.preventDefault()
        fn.handleApplyCoupon()
      }
    }
  },

  handleApplyCoupon: () => {
    props.$couponForm.classList.add('is-loading')
    props.$couponField.disabled = true

    Array.from(props.$couponForm.querySelectorAll('.coupon-status-text')).forEach(el => el.remove())

    const formData = new FormData()
    formData.append('_coupon', props.$couponField.value)
    formData.append('SecurityID', props.formSecurityId)

    ajax.post(props.handleCouponEndpoint, formData).then(resp => {
      props.$couponForm.classList.remove('is-loading')

      if (resp.data.success) {
        const $applied = document.createRange().createContextualFragment(`
            <div class="coupon-status-text">
              Applied coupon <strong>${resp.data.coupon_code}</strong> ($${resp.data.coupon_total} off)
            </div>
          `)

        props.$couponForm.appendChild($applied)
        props.$couponForm.classList.add('has-applied')

        const hiddenInput = document.createElement('input')
        hiddenInput.setAttribute('type', 'hidden')
        hiddenInput.setAttribute('name', 'CouponID')
        hiddenInput.setAttribute('value', resp.data.coupon_id)
        props.$checkoutForm.appendChild(hiddenInput)

        props.couponId = resp.data.coupon_id

        fn.updateSummaryTotals()
      } else {
        const $error = document.createRange().createContextualFragment(`
            <div class="coupon-status-text coupon-status-text--error">
              Invalid coupon code
            </div>
          `)

        props.$couponForm.appendChild($error)
        props.$couponField.disabled = false
      }
    })
  },

  handleClearCoupon: () => {
    props.$couponForm.classList.add('is-loading')

    props.couponId = ''

    fn.updateSummaryTotals().then(() => {
      props.$couponForm.classList.remove('is-loading')
      props.$couponForm.classList.remove('has-applied')
      props.$couponField.value = null
      props.$couponField.disabled = false
      props.$checkoutForm.querySelector('input[name=CouponID]').remove()

      Array.from(props.$couponForm.querySelectorAll('.coupon-status-text')).forEach(el => el.remove())

      fn.handleCouponField()
    })
  },

  toggleCouponTotal: isVisible => {
    Array.from(document.querySelectorAll('.store-checkout-summary-totals__coupon')).forEach(
      el => (el.style.display = isVisible ? 'flex' : 'none')
    )
  },

  initializePaymentForm: async (form, stripeInstance) => {
    stripe = stripeInstance;

    const appearance = {
        labels: "floating",
        theme: "stripe",
        variables: {
            colorPrimary: "#158F58",
            colorBackground: "#ffffff",
            colorDanger: "#DE1D1D",
        },
    };

    try {
        const endpoint = form.dataset.paymentEndpoint;
        const pageTitle = document.title

        let amount = document.querySelector(SELECTORS.asClass('summaryTotalPrice')).textContent;

        const data = new FormData(form);
        data.set("amount", parseFloat(amount.replaceAll(',', '')));
        data.set("pageTitle", pageTitle)

        const res = await fetch(endpoint, {
          method: 'POST',
          body: data,
        }).then(response => response.json());

        elements = stripe.elements({ clientSecret: res.clientSecret, appearance });
        // Get the payment intent id during initialization
        paymentIntentId = res.id;

        paymentElement = elements.create("payment", {
          layout: "tabs",
        });

        paymentElement.mount("#payment-element");
      } catch (error) {
        console.error(error);
      }
  },

  updatePaymentIntent: async (amount) => {
    let form = props.$checkoutForm;

    const updateEndpoint = form.dataset.paymentEndpoint + "/handleamountupdate";

    const data = new FormData(form);
    data.set("amount", parseFloat(amount.replaceAll(',', '')));
    data.set("paymentIntentId", paymentIntentId);

    await fetch(updateEndpoint, {
        method: 'POST',
        body: data,
    }).then(response => response.json());

    paymentElement.update()
  },

  handleFormSubmit: () => {
    return new Promise((resolve, reject) => {
      const $form = props.$checkoutForm

      const $errorMessage = $form.querySelector('.form-error-message')
      $errorMessage.style.display = 'none'

      const $submitButton = $form.querySelector('[type=submit]')
      $submitButton.disabled = true
      $submitButton.classList.add(SELECTORS.loading)

      // Set the payment intent id
      let paymentIntentHiddenField = document.querySelector(".stripe-payment-intent-id");
      paymentIntentHiddenField.value = paymentIntentId;

      ajax
        .post($form.action, new FormData($form))
        .then(resp => {
          if (resp.data.success) {
            redirectURL = resp.data.redirect
            resolve()
          } else {
            $errorMessage.innerHTML = resp.data.message
            $errorMessage.style.display = 'block'
            $submitButton.disabled = false
            $submitButton.classList.remove(SELECTORS.loading)
            reject(new Error(resp.data.message))
          }
        })
        .catch((error) => {
          $errorMessage.innerHTML = 'Sorry, there was a problem with your submission'
          $errorMessage.style.display = 'block'
          $submitButton.disabled = false
          $submitButton.classList.remove(SELECTORS.loading)
          reject(error)
        })
    })
  },

  handleCheckout: async(e) => {
    e.preventDefault()

    const form = e.target;

    let valid = props.pristine.validate()
    props.$checkoutForm.classList.add(SELECTORS.validating)

    if (props.requiresShipping) {
      const carrier = document.querySelector(`${SELECTORS.shippingCarrier}:checked`)
      const carrierRate = document.querySelector(`${SELECTORS.shippingRateField}:checked`)
      const invalidCarrierRate = !carrier || !carrierRate

      props.$shippingRatesFieldset.classList.remove('has-error')

      if (!carrier || (carrier.value !== 'None' && invalidCarrierRate)) {
        props.$shippingRatesFieldset.classList.add('has-error')
        valid = false
      }
    }

    // Special case for membership setup component
    const $invalidMemberBlock = document.querySelector('.checkout-member-block.is-invalid')
    if ($invalidMemberBlock) {
      scrollTo($invalidMemberBlock, 150)
      return false
    }

    if (!valid) {
      const $firstError = props.$checkoutForm.querySelector('.field.has-error')
      if ($firstError) {
        scrollTo($firstError, 150, () => $firstError.querySelector('input').focus())
      }
    } else {
      try {
        await fn.handleFormSubmit()

        console.log('running rest now')

        const formData = new FormData(form);
        const billingDetails = {
          name: formData.get("BillingFullName"),
          email: formData.get("BillingEmail"),
          phone: formData.get("BillingPhone"),
          address: {
            line1: formData.get("BillingAddress"),
            line2: formData.get("BillingAddress2"),
            city: formData.get("BillingCity"),
            state: formData.get("BillingState"),
          }
        };

        const $submitButton = form.querySelector('[type=submit]')
        $submitButton.disabled = true;
        $submitButton.classList.add(SELECTORS.loading);

        let returnURL = window.location.origin + "/_success";

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnURL,
            payment_method_data: {
              billing_details: billingDetails,
            },
          },
          redirect: "if_required",
        });

        if (error && (error.type === "card_error" || error.type === "validation_error")) {
          fn.showMessage(error.message);
          $submitButton.disabled = false;
          $submitButton.classList.remove(SELECTORS.loading);
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
          // Redirect to success page
          window.location.href = redirectURL;
        } else {
          fn.showMessage("An unexpected error occurred.");
          $submitButton.disabled = false;
          $submitButton.classList.remove(SELECTORS.loading);
        }
      } catch (error) {
        console.error(error);
        fn.showMessage("An unexpected error occurred.");
      }
    }

    return false
  },

  showMessage: (messageText) => {
    const messageContainer = document.querySelector("#payment-message");

    messageContainer.classList.remove("hidden");
    messageContainer.textContent = messageText;
    messageContainer.style.color = "red";

    setTimeout(function () {
        messageContainer.classList.add("hidden");
        messageContainer.textContent = "";
    }, 4000);
  },

  handleTaxId: () => {

    if (props.shippingCountry != 'United States') {
      props.$taxIdHolder.style.display = 'block'
      document.getElementById('CheckoutForm_CheckoutForm_TaxID').setAttribute('required', 'required')
      document.getElementById('CheckoutForm_CheckoutForm_TaxID').setAttribute('aria-required', 'true')
      props.pristine = new Pristine(props.$checkoutForm, props.pristineConfig)
    }

    if (document.querySelector('#CheckoutForm_CheckoutForm_ShippingCarrier_None')) {
      if (document.querySelector('#CheckoutForm_CheckoutForm_ShippingCarrier_None').checked) {
        props.$taxIdHolder.style.display = 'none'
        document.getElementById('CheckoutForm_CheckoutForm_TaxID').removeAttribute('required')
        document.getElementById('CheckoutForm_CheckoutForm_TaxID').removeAttribute('aria-required')
        props.pristine = new Pristine(props.$checkoutForm, props.pristineConfig)
      }
    }

  },

  disablePayment: () => {
    document.querySelector('.store-checkout-payment').style.display = 'none'
  },

  enablePayment: () => {
    document.querySelector('.store-checkout-payment').style.display = 'block'
  },
}

export default {
  can: () => props.$checkoutPage,
  run: fn.init,
}
